import { gql, Resolver } from '@apollo/client'; // eslint-disable-line

import { taskUploadToS3 } from './taskUploadToS3';
import { taskCreateTrqkAudio } from './taskCreateTrqkAudio';

interface Args {
    bucketName: string;
    bucketRegion: string;
    file: File | S3UploadFile;
    identityPoolId: string;
    taskId: string;
    trqkDirectoryUrl: string;
    trqkSongId: string;
}

interface Context {
    bindManagedUpload?: BindManagedUpload;
    generateS3Key?: GenerateS3Key;
    getToken: GetToken;
    sendTaskEvent: SendTaskEvent;
}

export const taskUploadAudioFileToTrack: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    context: Context,
): Promise<UploadAudioFileToTrackOutput> => {
    const {
        bucketName,
        bucketRegion,
        file,
        identityPoolId,
        taskId,
        trqkDirectoryUrl,
        trqkSongId,
    } = args;
    const { bindManagedUpload, getToken, generateS3Key, sendTaskEvent } =
        context;

    // 1. Upload audio file to S3

    let uploadResult;

    try {
        uploadResult = await taskUploadToS3(
            obj,
            {
                bucketName,
                bucketRegion,
                file,
                doTaskUpdates: {
                    error: false,
                    success: false,
                    progress: true,
                },
                progressShare: 95,
                identityPoolId,
                taskId,
            },
            {
                bindManagedUpload,
                generateS3Key,
                sendTaskEvent,
            },
        );
    } catch (error) {
        sendTaskEvent({
            type: 'ERROR',
            value: {
                taskId,
                error: `Failed to upload file to S3: ${file.name}`,
            },
        });

        throw error;
    }

    const { location: assetUrl, filename } = uploadResult;

    if (uploadResult.aborted || !uploadResult.completed) {
        return {
            assetUrl: null,
            filename,
            trqkAudio: null,
            trqkSongId,
            uploadSuccess: false,
            createTrqkAudioSuccess: false,
        };
    }

    // 2. Create a trqkAudio with the newly uploaded audio file

    const createTrqkAudioResult = await taskCreateTrqkAudio(
        obj,
        {
            assetUrl,
            filename,
            doTaskUpdates: {
                error: true,
                success: true,
                progress: true,
            },
            trqkSongId,
            taskId,
            trqkDirectoryUrl,
        },
        {
            getToken,
            sendTaskEvent,
        },
    );

    return {
        assetUrl,
        filename,
        trqkAudio: createTrqkAudioResult,
        trqkSongId,
        uploadSuccess: true,
        createTrqkAudioSuccess: true,
    };
};

export const TASK_UPLOAD_AUDIO_FILE_TO_TRACK = gql`
    mutation TaskUploadAudioFileToTrack(
        $bucketName: String!
        $bucketRegion: String!
        $identityPoolId: String!
        $file: Upload!
        $taskId: ID!
        $trqkDirectoryUrl: String!
        $trqkSongId: ID!
    ) {
        taskUploadAudioFileToTrack(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            file: $file
            taskId: $taskId
            trqkDirectoryUrl: $trqkDirectoryUrl
            trqkSongId: $trqkSongId
        ) @client
    }
`;
